import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, MapPin, Navigation, Phone, Search, Store } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { districtName } from "../../data/districts";
import { serviceLabel } from "../../data/services";
import { useLang } from "../../i18n/LangContext";
import { Reveal, Overline } from "./Reveal";
import { scrollToId } from "./SmoothScroll";

const mapsUrl = (s) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${s.name}, ${s.address}, ${s.pincode}`)}`;

export const FindShop = () => {
  const { t, lang } = useLang();
  const f = t.find;
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  const search = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.get("/shops/near", { params: { pincode: pin } });
      setRes({ match: data?.match ?? "none", shops: Array.isArray(data?.shops) ? data.shops : [] });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="find" className="pb-24 lg:pb-32" data-testid="find-shop-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal className="rounded-[32px] border border-white/10 bg-card p-7 sm:p-10 lg:p-14">
          <div className="grid gap-8 lg:grid-cols-2 lg:items-end">
            <div>
              <Overline>{f.over}</Overline>
              <h2 className="section-title mt-5">{f.title}</h2>
              <p className="mt-4 text-slate-300">{f.sub}</p>
            </div>
            <form onSubmit={search} className="flex flex-col gap-4 sm:flex-row sm:items-end" data-testid="find-shop-form">
              <label className="flex-1">
                <span className="text-xs font-medium uppercase tracking-wider text-slate-400">{f.pin}</span>
                <input required inputMode="numeric" maxLength={6} pattern="[0-9]{6}" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                  className="field keep-mono mt-2 font-mono text-2xl tracking-[0.3em]" placeholder="497231" data-testid="find-shop-pincode-input" />
              </label>
              <button type="submit" disabled={busy} className="btn-primary btn-lg justify-center" data-testid="find-shop-btn">
                {busy ? <><Loader2 size={18} className="animate-spin" /> {f.searching}</> : <><Search size={18} /> {f.search}</>}
              </button>
            </form>
          </div>

          {res && (
            <div className="mt-10 border-t border-white/10 pt-8" data-testid="find-shop-results">
              {res.shops.length ? (
                <>
                  <p className="text-sm text-orange-300" data-testid="find-shop-match">{f[res.match]}</p>
                  <div className="mt-5 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {res.shops.map((s, i) => (
                      <motion.div key={s.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                        className="flex flex-col rounded-[20px] border border-white/10 bg-ink p-5" data-testid={`find-shop-result-${i}`}>
                        <div className="flex items-start gap-3">
                          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-saffron/15 text-saffron"><Store size={18} /></span>
                          <div className="min-w-0">
                            <p className="font-display text-lg font-semibold leading-tight text-white">{s.name}</p>
                            <p className="mt-1 text-xs text-slate-400">{districtName(s.district, lang)} · <span className="font-mono">{s.pincode}</span></p>
                          </div>
                        </div>
                        <p className="mt-4 flex items-start gap-2 text-sm text-slate-300"><MapPin size={14} className="mt-0.5 shrink-0 text-saffron" />{s.address}</p>
                        <div className="mt-4 flex flex-1 flex-wrap content-start gap-1.5" data-testid={`find-shop-services-${i}`}>
                          {(Array.isArray(s.services) ? s.services : []).map((k) => (
                            <span key={k} className="rounded-full bg-emerald/10 px-2.5 py-1 text-[11px] text-emerald-300">{serviceLabel(k, lang)}</span>
                          ))}
                        </div>
                        <div className="mt-5 flex flex-wrap gap-2">
                          {s.phone && <a href={`tel:${s.phone.replace(/[^\d+]/g, "")}`} className="btn-ghost px-4 py-2 text-xs" data-testid={`find-shop-call-${i}`}><Phone size={13} /> {f.call}</a>}
                          <a href={mapsUrl(s)} target="_blank" rel="noopener noreferrer" className="btn-primary px-4 py-2 text-xs" data-testid={`find-shop-directions-${i}`}><Navigation size={13} /> {f.directions}</a>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between" data-testid="find-shop-none">
                  <p className="text-slate-200">{f.none}</p>
                  <button onClick={() => scrollToId("join")} className="btn-primary" data-testid="find-shop-become-btn">{f.become}</button>
                </div>
              )}
            </div>
          )}
        </Reveal>
      </div>
    </section>
  );
};
